import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Building2, ArrowRight, Phone, Car, Wrench, Home as HomeIcon,
  Sparkles, Zap, Languages, FileText, RectangleHorizontal,
  ShoppingBag, Package, Percent, TrendingUp, Star, Crown, Store,
  Key, DollarSign, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useAIChat } from '../config/aiChatContext';
import { useCompare } from '../config/compareContext';
import { PriceDisplay } from '../components/PriceDisplay';
import PlateVisual from '../components/plates/PlateVisual';
import { getDisplayPlateNumber } from '../utils/digitMask';
import ItemDetailsModal from '../components/ItemDetailsModal';
import { toast } from 'sonner';
import axios from 'axios';

// Extracted components
import { HorizontalSlider, NumberCard, LoyaltyCard, getLoyaltyTiers, HeroSection, SectionHeader } from '../components/home';
import { useHomeData, useFavorites } from '../hooks/useHomeData';
import { LOGO_DARK, LOGO_LIGHT, SERVICES_IMAGE } from '../constants/images';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { openChat } = useAIChat();
  const { toggleCompare, isInCompare, compareCount } = useCompare();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  
  // Data hooks
  const { latestItems, storeSections, marketplaceSections, businessItems, discountedItems, carsPriceDrops, ecommerceData, realEstateData, businessStores, personalNumbers, loading } = useHomeData();
  
  // User and favorites
  const [user, setUser] = useState(null);
  const { favorites, fetchFavorites, isFavorite } = useFavorites(user);
  
  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const toggleFavorite = async (item, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    if (!user) {
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }
    
    const token = localStorage.getItem('token');
    const itemId = item.id;
    const isCurrentlyFavorite = isFavorite(itemId);
    
    // Determine item type based on item properties
    let itemType = 'phone_number';
    if (item.item_type) {
      itemType = item.item_type;
    } else if (item.store_type === 'auto' || item.make || item.model) {
      itemType = 'vehicle';
    } else if (item.store_type === 'car_plates' || item.plateNumber) {
      itemType = 'car_plate';
    } else if (item.store_type === 'real_estate' || item.property_type || item.listingType) {
      itemType = 'real_estate';
    } else if (item.store_type === 'ecommerce') {
      itemType = 'product';
    }
    
    try {
      if (isCurrentlyFavorite) {
        await axios.delete(`${BACKEND_URL}/api/user/favorites/${itemType}/${itemId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isAr ? 'تمت الإزالة من المفضلة' : 'Removed from favorites');
      } else {
        await axios.post(`${BACKEND_URL}/api/user/favorites/${itemType}/${itemId}`, {
          item_data: {
            number: item.displayNumber || item.number,
            title: item.title || item.name || item.make,
            price: item.salePrice || item.price,
            store_name: item.store_name || item.store?.name || '',
            image: item.images?.[0] || item.image,
            location: item.location
          }
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isAr ? 'تمت الإضافة للمفضلة' : 'Added to favorites');
      }
      fetchFavorites();
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(isAr ? 'حدث خطأ' : 'An error occurred');
    }
  };

  const openItemModal = (item) => {
    // Navigate to details page instead of opening modal
    const itemId = item.id;
    const itemType = item.item_type || item.store_type;
    
    if (itemType === 'phone_number' || itemType === 'phone_numbers') {
      navigate(`/phone/${itemId}`);
    } else if (itemType === 'car' || itemType === 'auto' || item.make) {
      navigate(`/car/${itemId}`);
    } else if (itemType === 'car_plate' || itemType === 'car_plates' || item.plateNumber) {
      navigate(`/plate/${itemId}`);
    } else if (itemType === 'real_estate' || item.property_type || item.listingType) {
      navigate(`/property/${itemId}`);
    } else if (itemType === 'ecommerce' || itemType === 'product') {
      navigate(`/product/${itemId}`);
    } else {
      // Fallback to modal for unknown types
      setSelectedItem(item);
      setShowItemModal(true);
    }
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
  };

  // Services configuration
  const services = [
    { icon: Building2, title: isAr ? 'تأسيس الشركات' : 'Company Formation', desc: isAr ? 'أسس شركتك في رأس الخيمة' : 'Start your company in RAK', link: '/services/company-formation', color: 'from-blue-500 to-cyan-500' },
    { icon: Languages, title: isAr ? 'الترجمة القانونية' : 'Legal Translation', desc: isAr ? 'ترجمة معتمدة لجميع المستندات' : 'Certified document translation', link: '/services/translation', color: 'from-purple-500 to-pink-500' },
    { icon: FileText, title: isAr ? 'تخليص المستندات' : 'Document Clearing', desc: isAr ? 'خدمات حكومية سريعة' : 'Fast government services', link: '/services/document-clearing', color: 'from-amber-500 to-orange-500' }
  ];

  // Categories configuration
  const categories = [
    { icon: Phone, title: isAr ? 'أرقام مميزة' : 'Premium Numbers', count: latestItems.numbers?.length || 0, link: '/mobile-numbers', gradient: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-500/10', color: '#10B981' },
    { icon: RectangleHorizontal, title: isAr ? 'لوحات سيارات' : 'Car Plates', count: businessItems.car_plates?.length || 0, link: '/plates', gradient: 'from-amber-500 to-yellow-500', bg: 'bg-amber-500/10', color: '#F59E0B' },
    { icon: Car, title: isAr ? 'سيارات' : 'Cars', count: latestItems.cars?.length || 0, link: '/cars', gradient: 'from-blue-500 to-indigo-500', bg: 'bg-blue-500/10', color: '#1E9BD9' },
    { icon: Wrench, title: isAr ? 'قطع غيار' : 'Spare Parts', count: latestItems.spare_parts?.length || 0, link: '/spare-parts', gradient: 'from-orange-500 to-red-500', bg: 'bg-orange-500/10', color: '#F97316' }
  ];

  const loyaltyTiers = getLoyaltyTiers(isAr);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'} relative`} dir={isAr ? 'rtl' : 'ltr'}>
      
      {/* Watermark Background Logo */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <img 
          src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} 
          alt="" 
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.02]'}`}
        />
      </div>
      
      {/* Hero Section */}
      <HeroSection isDarkMode={isDarkMode} isAr={isAr} openChat={openChat} />

      {/* Services Section */}
      <section className={`py-10 sm:py-14 relative z-10 overflow-hidden ${isDarkMode ? 'bg-[#0A0B10]' : 'bg-[#F8FAFC]'}`} data-testid="home-services-section">
        <div className={`absolute inset-0 pointer-events-none ${isDarkMode ? 'cgm-dot-grid opacity-30' : 'cgm-dot-grid-light opacity-40'} [mask-image:linear-gradient(to_bottom,transparent,black_20%,black_80%,transparent)]`} />
        <div className={`absolute -top-24 start-1/3 w-[420px] h-[420px] rounded-full blur-[120px] pointer-events-none ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/10'}`} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8 sm:mb-10">
            <div className="max-w-2xl">
              <div className={`inline-flex items-center gap-2 border px-4 py-1.5 rounded-full mb-4 ${isDarkMode ? 'bg-[#1E9BD9]/10 border-[#1E9BD9]/30' : 'bg-white border-[#1E9BD9]/30 shadow-sm'}`}>
                <Zap className="h-4 w-4 text-[#1E9BD9]" />
                <span className="text-xs sm:text-sm font-semibold text-[#1E9BD9]">{isAr ? 'خدماتنا الحكومية والشركات' : 'Corporate & government services'}</span>
              </div>
              <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {isAr ? 'خدمات احترافية متكاملة ' : 'Professional integrated services '}
                <span className="cgm-brand-text">{isAr ? 'يديرها مساعد ذكي' : 'run by a smart assistant'}</span>
              </h2>
              <p className={`mt-3 text-sm sm:text-base ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {isAr ? 'ابدأ أي خدمة بمحادثة — يجمع المساعد المستندات، يحسب التكلفة، ويفتح لك الطلب رسمياً.' : 'Start any service with a chat — the assistant collects documents, estimates cost and opens your request.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => openChat(isAr ? 'أريد معرفة الخدمات المتاحة وأسعارها' : 'Show me the available services and prices')}
              className={`self-start lg:self-auto inline-flex items-center gap-2 h-11 px-5 rounded-xl text-sm font-bold border transition-all hover:-translate-y-0.5 ${isDarkMode ? 'cgm-glass-dark text-white hover:border-[#1E9BD9]/60' : 'cgm-glass-light text-[#0F172A] hover:border-[#1E9BD9]/60'}`}
              data-testid="services-ask-ai-btn"
            >
              <Sparkles className="h-4 w-4 text-[#F5A962]" />
              {isAr ? 'اسأل المساعد عن الخدمات' : 'Ask the assistant'}
            </button>
          </div>

          <div className="grid md:grid-cols-3 gap-5 sm:gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className={`group relative rounded-3xl p-7 sm:p-8 overflow-hidden cgm-card-glow animate-slideUp ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light'}`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className="absolute inset-x-8 top-0 cgm-scanline opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className={`absolute -bottom-16 -end-16 w-44 h-44 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br ${service.color}`} style={{ opacity: 0.18 }} />
                <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                  <service.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className={`relative text-lg sm:text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{service.title}</h3>
                <p className={`relative mb-7 text-sm leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{service.desc}</p>
                <div className="relative flex items-center justify-between">
                  <span className="inline-flex items-center text-[#1E9BD9] text-sm font-semibold group-hover:gap-3 transition-all">
                    {isAr ? 'ابدأ الآن' : 'Start now'}
                    <ArrowRight className={`h-4 w-4 ms-2 transition-transform group-hover:translate-x-1 ${isAr && 'rotate-180'}`} />
                  </span>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full ${isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                    <Sparkles className="h-3 w-3 text-[#F5A962]" />
                    AI
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Marketplace Categories */}
      <section className={`py-10 sm:py-14 z-10 relative ${isDarkMode ? 'bg-[#0d0e14]' : 'bg-white'}`} data-testid="home-categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <div className={`inline-flex items-center gap-2 border px-4 py-1.5 rounded-full mb-4 ${isDarkMode ? 'bg-[#F59E0B]/10 border-[#F59E0B]/30' : 'bg-[#F59E0B]/5 border-[#F59E0B]/30'}`}>
              <Sparkles className="h-4 w-4 text-[#F59E0B]" />
              <span className="text-xs sm:text-sm text-[#F59E0B] font-semibold">{isAr ? 'السوق' : 'Marketplace'}</span>
            </div>
            <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {isAr ? 'تصفح أقسام السوق' : 'Browse the marketplace'}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {categories.map((cat, index) => (
              <Link
                key={index}
                to={cat.link}
                className={`group relative rounded-3xl p-5 sm:p-7 overflow-hidden text-center cgm-card-glow animate-slideUp ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light'}`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`category-tile-${index}`}
              >
                <div className="absolute -top-12 -end-12 w-32 h-32 rounded-full blur-3xl opacity-25 transition-opacity duration-500 group-hover:opacity-60" style={{ background: cat.color }} />
                <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 border transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3" style={{ background: `${cat.color}1a`, borderColor: `${cat.color}40` }}>
                  <cat.icon className="h-8 w-8" style={{ color: cat.color }} />
                </div>
                <h3 className={`relative text-base sm:text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{cat.title}</h3>
                <p className={`relative text-xs sm:text-sm tabular-nums ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{cat.count}+ {isAr ? 'إعلان' : 'listings'}</p>
                <div className="relative mt-4 mx-auto h-[2px] w-10 rounded-full transition-all duration-500 group-hover:w-20" style={{ background: `linear-gradient(90deg, ${cat.color}, #F5A962)` }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers Section - أرقام مميزة */}
      <section className={`py-5 sm:py-6 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            icon={Phone}
            accent="#10B981"
            eyebrow={isAr ? 'أرقام مميزة' : 'Premium Numbers'}
            title={isAr ? 'الأرقام المميزة' : 'Premium Numbers'}
            link="/mobile-numbers"
            isDarkMode={isDarkMode}
            isAr={isAr}
            testId="home-section-mobile-numbers"
          />

          {/* New Arrivals */}
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-emerald-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'وصل حديثاً' : 'New Arrivals'}
              </h3>
              {businessItems.phone_numbers.length > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-[#1E9BD9]/20 text-[#1E9BD9]' : 'bg-[#1E9BD9]/10 text-[#1E9BD9]'}`}>
                  {businessItems.phone_numbers.length} {isAr ? 'من المتاجر' : 'from stores'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/mobile-numbers" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {businessItems.phone_numbers.map((item) => (
                <NumberCard 
                  key={`business-${item.id}`}
                  item={item}
                  isDarkMode={isDarkMode}
                  isAr={isAr}
                  isInCompare={isInCompare}
                  toggleCompare={toggleCompare}
                  onOpenModal={openItemModal}
                  variant="business"
                />
              ))}
              {/* Personal Numbers - أرقام شخصية */}
              {personalNumbers.map((item) => (
                <NumberCard 
                  key={`personal-${item.id}`}
                  item={{...item, store_name: item.seller_name}}
                  isDarkMode={isDarkMode}
                  isAr={isAr}
                  isInCompare={isInCompare}
                  toggleCompare={toggleCompare}
                  onOpenModal={openItemModal}
                  variant="personal"
                />
              ))}
              {marketplaceSections.numbers?.new?.map((item) => (
                <NumberCard 
                  key={`mp-${item.id}`}
                  item={item}
                  isDarkMode={isDarkMode}
                  isAr={isAr}
                  isInCompare={isInCompare}
                  toggleCompare={toggleCompare}
                  onOpenModal={openItemModal}
                  variant="marketplace"
                />
              ))}
            </HorizontalSlider>
          </div>

          {/* Discounted Numbers - عروض وخصومات */}
          <div className={`rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-gradient-to-r from-red-50 to-orange-50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <Percent className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'عروض وخصومات' : 'Special Offers'}
              </h3>
              {discountedItems.phone_numbers.length > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-500/10 text-red-600'}`}>
                  {discountedItems.phone_numbers.length} {isAr ? 'عرض' : 'offers'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/mobile-numbers?filter=discounted" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {discountedItems.phone_numbers.map((item) => (
                <NumberCard 
                  key={`disc-${item.id}`}
                  item={{
                    ...item,
                    original_price: item.price || item.originalPrice,
                    price: item.salePrice,
                    discount_percentage: item.discountPercentage
                  }}
                  isDarkMode={isDarkMode}
                  isAr={isAr}
                  isInCompare={isInCompare}
                  toggleCompare={toggleCompare}
                  onOpenModal={openItemModal}
                  variant="discounted"
                />
              ))}
              {marketplaceSections.numbers?.price_updated?.map((item) => (
                <NumberCard 
                  key={item.id}
                  item={item}
                  isDarkMode={isDarkMode}
                  isAr={isAr}
                  isInCompare={isInCompare}
                  toggleCompare={toggleCompare}
                  onOpenModal={openItemModal}
                  variant="price_updated"
                />
              ))}
            </HorizontalSlider>
          </div>
        </div>
      </section>

      {/* Plates Section */}
      <section className={`py-5 sm:py-6 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            icon={RectangleHorizontal}
            accent="#06B6D4"
            eyebrow={isAr ? 'لوحات السيارات' : 'Car Plates'}
            title={isAr ? 'لوحات أرقام السيارات' : 'Car License Plates'}
            link="/plates"
            isDarkMode={isDarkMode}
            isAr={isAr}
            testId="home-section-plates"
          />

          {/* New Plates from CloudGM Business */}
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-cyan-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-cyan-500 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'وصل حديثاً' : 'New Arrivals'}
              </h3>
              {businessItems.car_plates.length > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-500/10 text-cyan-600'}`}>
                  {businessItems.car_plates.length} {isAr ? 'من المتاجر' : 'from stores'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/plates" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {businessItems.car_plates.map((item) => (
                <div 
                  key={`plate-${item.id}`} 
                  onClick={() => openItemModal({ ...item, item_type: 'car_plate' })}
                  className={`flex-shrink-0 w-48 sm:w-56 rounded-xl p-3 text-center transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-cyan-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-cyan-500/50'}`}
                >
                  {/* UAE Plate Design (official) */}
                  <div className="mb-3 flex justify-center items-center">
                    <PlateVisual emirate={item.emirate} category={item.plateType} code={item.plateCode} number={getDisplayPlateNumber(item) || item.plateNumber} width={168} showCodeOnPlate={item.show_on_plate} />
                  </div>
                  <div className="text-cyan-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                </div>
              ))}
              {marketplaceSections.plates?.new?.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => openItemModal({ ...item, item_type: 'car_plate' })}
                  className={`flex-shrink-0 w-36 sm:w-44 rounded-xl p-3 text-center transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-cyan-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-cyan-500/50'}`}
                >
                  <div className={`text-xs mb-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{item.emirate}</div>
                  <div className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`} style={{ direction: 'ltr' }}>{item.plate_number}</div>
                  <div className="text-cyan-500 font-bold text-xs"><PriceDisplay price={item.price} /></div>
                </div>
              ))}
            </HorizontalSlider>
          </div>

          {/* Discounted Plates - عروض وخصومات */}
          <div className={`rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-gradient-to-r from-red-50 to-orange-50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <Percent className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'عروض وخصومات' : 'Special Offers'}
              </h3>
              {discountedItems.car_plates.length > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-500/10 text-red-600'}`}>
                  {discountedItems.car_plates.length} {isAr ? 'عرض' : 'offers'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/plates?filter=discounted" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {discountedItems.car_plates.map((item) => (
                <div 
                  key={`disc-plate-${item.id}`} 
                  onClick={() => openItemModal({ ...item, item_type: 'car_plate', original_price: item.price, price: item.salePrice })}
                  className={`flex-shrink-0 w-48 sm:w-56 rounded-xl p-3 text-center transition-all hover:scale-105 origin-top cursor-pointer relative overflow-hidden ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-red-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-red-500/50'}`}
                >
                  {/* Discount Badge */}
                  <div className="absolute top-2 end-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                    -{Math.round(item.discountPercentage)}%
                  </div>
                  
                  {/* UAE Plate Design (official) */}
                  <div className="mb-3 flex justify-center items-center">
                    <PlateVisual emirate={item.emirate} category={item.plateType} code={item.plateCode} number={getDisplayPlateNumber(item) || item.plateNumber} width={168} showCodeOnPlate={item.show_on_plate} />
                  </div>
                  
                  {/* Prices */}
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-red-500 font-bold text-sm"><PriceDisplay price={item.salePrice} /></span>
                    <span className="text-gray-400 line-through text-xs"><PriceDisplay price={item.price} /></span>
                  </div>
                </div>
              ))}
              {marketplaceSections.plates?.price_updated?.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => openItemModal({ ...item, item_type: 'car_plate' })}
                  className={`flex-shrink-0 w-36 sm:w-44 rounded-xl p-3 text-center transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-orange-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-orange-500/50'}`}
                >
                  <div className={`text-xs mb-1 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{item.emirate}</div>
                  <div className={`text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`} style={{ direction: 'ltr' }}>{item.plate_number}</div>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-orange-500 font-bold text-xs"><PriceDisplay price={item.price} /></span>
                    {item.original_price && <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={item.original_price} /></span>}
                  </div>
                </div>
              ))}
            </HorizontalSlider>
          </div>
        </div>
      </section>

      {/* Cars Section */}
      <section className={`py-5 sm:py-6 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            icon={Car}
            accent="#1E9BD9"
            eyebrow={isAr ? 'السيارات' : 'Cars'}
            title={isAr ? 'سيارات للبيع' : 'Cars for Sale'}
            link="/cars"
            isDarkMode={isDarkMode}
            isAr={isAr}
            testId="home-section-cars"
          />

          {/* New Cars */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-blue-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'وصل حديثاً' : 'New Arrivals'}
              </h3>
              {businessItems.auto.length > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-500/10 text-blue-600'}`}>
                  {businessItems.auto.length} {isAr ? 'سيارة' : 'cars'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/cars" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {/* Business Cars from CloudGM Business */}
              {businessItems.auto.map((car, index) => {
                const placeholderImages = [
                  'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&q=80',
                  'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&q=80',
                  'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400&q=80'
                ];
                const carImage = car.images?.[0] || car.image || placeholderImages[index % 3];
                const isValidImage = carImage && (carImage.startsWith('http') || carImage.startsWith('data:') || carImage.startsWith('/'));
                
                return (
                  <div 
                    key={`business-car-${car.id}`}
                    onClick={() => openItemModal({ ...car, item_type: 'car' })}
                    className={`flex-shrink-0 w-40 sm:w-48 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-blue-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-blue-500/50'}`}
                  >
                    <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                      {isValidImage ? (
                        <img src={carImage} alt={car.make} className="w-full h-full object-cover" />
                      ) : (
                        <img src={placeholderImages[index % 3]} alt={car.make} className="w-full h-full object-cover" />
                      )}
                      {car.condition && (
                        <div className={`absolute top-2 start-2 text-white text-[10px] px-2 py-0.5 rounded-full ${car.condition === 'new' ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                          {car.condition === 'new' ? (isAr ? 'جديد' : 'New') : (isAr ? 'مستعمل' : 'Used')}
                        </div>
                      )}
                      {car.year && (
                        <div className="absolute top-2 end-2 bg-black/50 backdrop-blur-sm text-white text-[10px] px-2 py-0.5 rounded-full">
                          {car.year}
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                        {car.make} {car.model}
                      </h4>
                      {car.hasDiscount && car.salePrice ? (
                        <div className="flex items-center gap-1">
                          <span className="text-red-500 font-bold text-sm"><PriceDisplay price={car.salePrice} /></span>
                          <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={car.price} /></span>
                        </div>
                      ) : (
                        <div className="text-blue-500 font-bold text-sm"><PriceDisplay price={car.price} /></div>
                      )}
                      {car.store_name && (
                        <div className={`flex items-center gap-1 mt-1 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Store className="h-3 w-3" />
                          <span className="truncate">{car.store_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {marketplaceSections.cars?.new?.map((item) => (
                <Link key={item.id} to={`/marketplace/cars/${item.id}`} className={`flex-shrink-0 w-40 sm:w-48 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
                  <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.images?.[0] ? <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Car className={`h-8 w-8 ${isDarkMode ? 'text-blue-500/30' : 'text-blue-500/20'}`} /></div>}
                    <div className="absolute top-2 start-2 bg-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full">{isAr ? 'جديد' : 'New'}</div>
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>{item.title}</h4>
                    <div className="text-blue-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                  </div>
                </Link>
              ))}
            </HorizontalSlider>
          </div>
          )}

          {/* Cars Price Drops - تم تعديل السعر */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-gradient-to-r from-orange-50 to-amber-50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'تم تعديل السعر' : 'Price Updated'}
              </h3>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-500/10 text-orange-600'}`}>
                {carsPriceDrops.length} {isAr ? 'سيارة' : 'cars'}
              </span>
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/cars?filter=price-drops" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {carsPriceDrops.map((car, index) => {
                const placeholderImages = [
                  'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=400&q=80',
                  'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&q=80',
                  'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400&q=80'
                ];
                const carImage = car.images?.[0] || car.image || placeholderImages[index % 3];
                const isValidImage = carImage && (carImage.startsWith('http') || carImage.startsWith('data:') || carImage.startsWith('/'));
                
                return (
                  <div 
                    key={`price-drop-car-${car.id}`}
                    onClick={() => openItemModal({ ...car, item_type: 'car', seller_type: 'store' })}
                    className={`flex-shrink-0 w-40 sm:w-48 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer relative ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-orange-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-orange-500/50'}`}
                  >
                    <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                      {isValidImage ? (
                        <img src={carImage} alt={car.make} className="w-full h-full object-cover" />
                      ) : (
                        <img src={placeholderImages[index % 3]} alt={car.make} className="w-full h-full object-cover" />
                      )}
                      <div className="absolute top-2 start-2 bg-orange-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                        {isAr ? 'سعر جديد' : 'New Price'}
                      </div>
                      <div className="absolute top-2 end-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        -{car.discountPercentage}%
                      </div>
                    </div>
                    <div className="p-2.5">
                      <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                        {car.make} {car.model}
                      </h4>
                      <div className="flex items-center gap-1">
                        <span className="text-orange-500 font-bold text-sm"><PriceDisplay price={car.salePrice} /></span>
                        <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={car.price} /></span>
                      </div>
                      {car.store_name && (
                        <div className={`flex items-center gap-1 mt-1 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Store className="h-3 w-3" />
                          <span className="truncate">{car.store_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </HorizontalSlider>
          </div>
          )}
        </div>
      </section>

      {/* Real Estate Section - العقارات */}
      <section className={`py-5 sm:py-6 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            icon={HomeIcon}
            accent="#8B5CF6"
            eyebrow={isAr ? 'العقارات' : 'Real Estate'}
            title={isAr ? 'العقارات' : 'Real Estate'}
            link="/properties"
            isDarkMode={isDarkMode}
            isAr={isAr}
            testId="home-section-properties"
          />

          {/* للبيع - For Sale */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-emerald-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'للبيع' : 'For Sale'}
              </h3>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-500/10 text-emerald-600'}`}>
                {realEstateData.for_sale?.length || 0}
              </span>
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/properties?listing_type=for_sale" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {(realEstateData.for_sale || []).map((item) => (
                <div
                  key={`sale-${item.id}`}
                  onClick={() => openItemModal({ ...item, item_type: 'real_estate' })}
                  className={`flex-shrink-0 w-44 sm:w-52 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-emerald-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-emerald-500/50'}`}
                >
                  <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.images?.[0] || item.image ? (
                      <img src={item.images?.[0] || item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <HomeIcon className={`h-8 w-8 ${isDarkMode ? 'text-emerald-500/30' : 'text-emerald-500/20'}`} />
                      </div>
                    )}
                    <div className="absolute top-2 start-2 bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                      {isAr ? 'للبيع' : 'For Sale'}
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                      {item.title || item.property_type}
                    </h4>
                    <div className="text-emerald-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                    {item.location && (
                      <div className={`flex items-center gap-1 mt-1 text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <MapPin className="h-2.5 w-2.5" />
                        <span className="truncate">{item.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </HorizontalSlider>
          </div>
          )}

          {/* للإيجار - For Rent */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-blue-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <Key className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'للإيجار' : 'For Rent'}
              </h3>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-500/10 text-blue-600'}`}>
                {realEstateData.for_rent?.length || 0}
              </span>
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/properties?listing_type=for_rent" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {(realEstateData.for_rent || []).map((item) => (
                <div
                  key={`rent-${item.id}`}
                  onClick={() => openItemModal({ ...item, item_type: 'real_estate' })}
                  className={`flex-shrink-0 w-44 sm:w-52 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-blue-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-blue-500/50'}`}
                >
                  <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.images?.[0] || item.image ? (
                      <img src={item.images?.[0] || item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <HomeIcon className={`h-8 w-8 ${isDarkMode ? 'text-blue-500/30' : 'text-blue-500/20'}`} />
                      </div>
                    )}
                    <div className="absolute top-2 start-2 bg-blue-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                      {isAr ? 'للإيجار' : 'For Rent'}
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                      {item.title || item.property_type}
                    </h4>
                    <div className="flex items-center gap-1">
                      <span className="text-blue-500 font-bold text-sm"><PriceDisplay price={item.price} /></span>
                      <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>/{isAr ? 'سنة' : 'year'}</span>
                    </div>
                    {item.location && (
                      <div className={`flex items-center gap-1 mt-1 text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <MapPin className="h-2.5 w-2.5" />
                        <span className="truncate">{item.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </HorizontalSlider>
          </div>
          )}

          {/* للاستثمار - For Investment */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-amber-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'للاستثمار' : 'For Investment'}
              </h3>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-500/10 text-amber-600'}`}>
                {realEstateData.for_investment?.length || 0}
              </span>
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/properties?listing_type=for_investment" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {(realEstateData.for_investment || []).map((item) => (
                <div
                  key={`invest-${item.id}`}
                  onClick={() => openItemModal({ ...item, item_type: 'real_estate' })}
                  className={`flex-shrink-0 w-44 sm:w-52 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-amber-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-amber-500/50'}`}
                >
                  <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.images?.[0] || item.image ? (
                      <img src={item.images?.[0] || item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <TrendingUp className={`h-8 w-8 ${isDarkMode ? 'text-amber-500/30' : 'text-amber-500/20'}`} />
                      </div>
                    )}
                    <div className="absolute top-2 start-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
                      {isAr ? 'للاستثمار' : 'Investment'}
                    </div>
                    {item.expected_roi && (
                      <div className="absolute bottom-2 end-2 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        ROI {item.expected_roi}%
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                      {item.title || item.property_type}
                    </h4>
                    <div className="text-amber-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                    {item.location && (
                      <div className={`flex items-center gap-1 mt-1 text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        <MapPin className="h-2.5 w-2.5" />
                        <span className="truncate">{item.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </HorizontalSlider>
          </div>
          )}

          {/* تم تعديل السعر - Price Updated (All Types) */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-gradient-to-r from-red-50 to-orange-50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <Percent className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'تم تعديل السعر' : 'Price Updated'}
              </h3>
              <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-500/10 text-red-600'}`}>
                {(realEstateData.price_updated?.length || 0) + (discountedItems.real_estate?.length || 0)} {isAr ? 'عرض' : 'offers'}
              </span>
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/properties?filter=discounted" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {[...(realEstateData.price_updated || []), ...(discountedItems.real_estate || [])].map((item) => (
                <div
                  key={`price-${item.id}`}
                  onClick={() => openItemModal({ ...item, item_type: 'real_estate', original_price: item.original_price || item.price, price: item.salePrice || item.price })}
                  className={`flex-shrink-0 w-44 sm:w-52 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer relative ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-red-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-red-500/50'}`}
                >
                  <div className={`aspect-video relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.images?.[0] || item.image ? (
                      <img src={item.images?.[0] || item.image} alt={item.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <HomeIcon className={`h-8 w-8 ${isDarkMode ? 'text-red-500/30' : 'text-red-500/20'}`} />
                      </div>
                    )}
                    {item.discountPercentage && (
                      <div className="absolute top-2 end-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        -{Math.round(item.discountPercentage)}%
                      </div>
                    )}
                    {/* Listing Type Badge */}
                    <div className={`absolute top-2 start-2 text-white text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      item.listingType === 'for_sale' ? 'bg-emerald-500' :
                      item.listingType === 'for_rent' ? 'bg-blue-500' :
                      item.listingType === 'for_investment' ? 'bg-amber-500' : 'bg-purple-500'
                    }`}>
                      {item.listingType === 'for_sale' ? (isAr ? 'للبيع' : 'Sale') :
                       item.listingType === 'for_rent' ? (isAr ? 'للإيجار' : 'Rent') :
                       item.listingType === 'for_investment' ? (isAr ? 'استثمار' : 'Invest') : (isAr ? 'عقار' : 'Property')}
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                      {item.title || item.property_type}
                    </h4>
                    <div className="flex items-center gap-1">
                      <span className="text-red-500 font-bold text-sm"><PriceDisplay price={item.salePrice || item.price} /></span>
                      {(item.original_price || (item.price && item.salePrice)) && (
                        <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={item.original_price || item.price} /></span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </HorizontalSlider>
          </div>
          )}
        </div>
      </section>

      {/* Store / Products Section */}
      <section className={`py-5 sm:py-6 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <SectionHeader
            icon={ShoppingBag}
            accent="#EC4899"
            eyebrow={isAr ? 'المتجر' : 'Store'}
            title={isAr ? 'المتجر الإلكتروني' : 'Online Store'}
            link="/products"
            isDarkMode={isDarkMode}
            isAr={isAr}
            testId="home-section-products"
          />

          {/* New Arrivals - E-commerce from CloudGM Business */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-green-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                <Package className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'وصل حديثاً' : 'New Arrivals'}
              </h3>
              {ecommerceData.new_arrivals?.length > 0 && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-500/10 text-green-600'}`}>
                  {ecommerceData.new_arrivals.length} {isAr ? 'منتج' : 'products'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/products?filter=new" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {/* Products from CloudGM Business */}
              {ecommerceData.new_arrivals?.map((item) => {
                const itemImage = item.images?.[0] || item.image;
                const isValidImage = itemImage && (itemImage.startsWith('http') || itemImage.startsWith('data:') || itemImage.startsWith('/'));
                return (
                  <div 
                    key={`ecom-${item.id}`} 
                    onClick={() => openItemModal({ ...item, item_type: 'ecommerce' })}
                    className={`flex-shrink-0 w-32 sm:w-40 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-green-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-green-500/50'}`}
                  >
                    <div className={`aspect-square relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                      {isValidImage ? (
                        <img src={itemImage} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className={`h-8 w-8 ${isDarkMode ? 'text-green-500/30' : 'text-green-500/20'}`} />
                        </div>
                      )}
                      <div className="absolute top-2 start-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                        {isAr ? 'جديد' : 'New'}
                      </div>
                    </div>
                    <div className="p-2.5">
                      <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                        {item.name}
                      </h4>
                      {item.hasDiscount && item.salePrice ? (
                        <div className="flex items-center gap-1">
                          <span className="text-red-500 font-bold text-sm"><PriceDisplay price={item.salePrice} /></span>
                          <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={item.price} /></span>
                        </div>
                      ) : (
                        <div className="text-green-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                      )}
                      {item.store_name && (
                        <div className={`flex items-center gap-1 mt-1 text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Store className="h-2.5 w-2.5" />
                          <span className="truncate">{item.store_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Fallback to old store sections */}
              {storeSections.new_arrivals?.map((item) => (
                <Link key={item.id} to={`/products/${item.id}`} className={`flex-shrink-0 w-32 sm:w-40 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
                  <div className={`aspect-square relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.image_url || item.images?.[0] ? <img src={item.image_url || item.images?.[0]} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className={`h-8 w-8 ${isDarkMode ? 'text-green-500/30' : 'text-green-500/20'}`} /></div>}
                    <div className="absolute top-2 start-2 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">{isAr ? 'جديد' : 'New'}</div>
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>{isAr ? item.name_ar || item.name : item.name}</h4>
                    <div className="text-green-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                  </div>
                </Link>
              ))}
            </HorizontalSlider>
          </div>
          )}

          {/* Discounts - E-commerce خصومات */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 mb-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-gradient-to-r from-red-50 to-orange-50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
                <Percent className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'خصومات' : 'Discounts'}
              </h3>
              {(ecommerceData.discounted?.length > 0 || discountedItems.ecommerce?.length > 0) && (
                <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-500/10 text-red-600'}`}>
                  {(ecommerceData.discounted?.length || 0) + (discountedItems.ecommerce?.length || 0)} {isAr ? 'عرض' : 'offers'}
                </span>
              )}
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/products?has_discount=true" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {/* Discounted Products from CloudGM Business */}
              {(ecommerceData.discounted || discountedItems.ecommerce || []).map((item) => {
                const itemImage = item.images?.[0] || item.image;
                const isValidImage = itemImage && (itemImage.startsWith('http') || itemImage.startsWith('data:') || itemImage.startsWith('/'));
                return (
                  <div 
                    key={`disc-ecom-${item.id}`} 
                    onClick={() => openItemModal({ ...item, item_type: 'ecommerce' })}
                    className={`flex-shrink-0 w-32 sm:w-40 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer relative ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-red-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-red-500/50'}`}
                  >
                    <div className={`aspect-square relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                      {isValidImage ? (
                        <img src={itemImage} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Percent className={`h-8 w-8 ${isDarkMode ? 'text-red-500/30' : 'text-red-500/20'}`} />
                        </div>
                      )}
                      {/* Discount Badge */}
                      <div className="absolute top-2 start-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        -{item.discountPercentage || Math.round(((item.price - item.salePrice) / item.price) * 100)}%
                      </div>
                    </div>
                    <div className="p-2.5">
                      <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                        {item.name}
                      </h4>
                      <div className="flex items-center gap-1">
                        <span className="text-red-500 font-bold text-sm"><PriceDisplay price={item.salePrice} /></span>
                        <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={item.price} /></span>
                      </div>
                      {item.store_name && (
                        <div className={`flex items-center gap-1 mt-1 text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Store className="h-2.5 w-2.5" />
                          <span className="truncate">{item.store_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </HorizontalSlider>
          </div>
          )}

          {/* Best Sellers - E-commerce */}
          {true && (
          <div className={`rounded-2xl p-4 sm:p-6 ${isDarkMode ? 'bg-[#141414]/50' : 'bg-amber-50/50'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#F59E0B] flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'الأكثر مبيعاً' : 'Best Sellers'}
              </h3>
            </div>
            <HorizontalSlider isDarkMode={isDarkMode} isRTL={isAr} viewAllLink="/products?filter=bestseller" viewAllText={isAr ? 'عرض الكل' : 'View All'}>
              {/* Bestsellers from CloudGM Business */}
              {ecommerceData.bestsellers?.map((item) => {
                const itemImage = item.images?.[0] || item.image;
                const isValidImage = itemImage && (itemImage.startsWith('http') || itemImage.startsWith('data:') || itemImage.startsWith('/'));
                return (
                  <div 
                    key={`ecom-best-${item.id}`} 
                    onClick={() => openItemModal({ ...item, item_type: 'ecommerce' })}
                    className={`flex-shrink-0 w-32 sm:w-40 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top cursor-pointer ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10 hover:border-amber-500/50' : 'bg-white border border-gray-200 shadow-sm hover:border-amber-500/50'}`}
                  >
                    <div className={`aspect-square relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                      {isValidImage ? (
                        <img src={itemImage} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <TrendingUp className={`h-8 w-8 ${isDarkMode ? 'text-amber-500/30' : 'text-amber-500/20'}`} />
                        </div>
                      )}
                      <div className="absolute top-2 start-2 bg-[#F59E0B] text-white text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                        <Star className="h-2.5 w-2.5 fill-current" />
                        {isAr ? 'الأكثر مبيعاً' : 'Bestseller'}
                      </div>
                    </div>
                    <div className="p-2.5">
                      <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                        {item.name}
                      </h4>
                      {item.hasDiscount && item.salePrice ? (
                        <div className="flex items-center gap-1">
                          <span className="text-red-500 font-bold text-sm"><PriceDisplay price={item.salePrice} /></span>
                          <span className="text-gray-400 line-through text-[10px]"><PriceDisplay price={item.price} /></span>
                        </div>
                      ) : (
                        <div className="text-amber-500 font-bold text-sm"><PriceDisplay price={item.price} /></div>
                      )}
                      {item.store_name && (
                        <div className={`flex items-center gap-1 mt-1 text-[9px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Store className="h-2.5 w-2.5" />
                          <span className="truncate">{item.store_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {storeSections.bestsellers?.map((item) => (
                <Link key={item.id} to={`/products/${item.id}`} className={`flex-shrink-0 w-32 sm:w-40 rounded-xl overflow-hidden transition-all hover:scale-105 origin-top ${isDarkMode ? 'bg-[#1a1a1a] border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
                  <div className={`aspect-square relative ${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'}`}>
                    {item.image_url || item.images?.[0] ? <img src={item.image_url || item.images?.[0]} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><TrendingUp className={`h-8 w-8 ${isDarkMode ? 'text-[#F59E0B]/30' : 'text-[#F59E0B]/20'}`} /></div>}
                    <div className="absolute top-2 start-2 bg-[#F59E0B] text-white text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                      <Star className="w-2 h-2 fill-current" />
                      {isAr ? 'مميز' : 'Top'}
                    </div>
                  </div>
                  <div className="p-2.5">
                    <h4 className={`font-medium text-xs line-clamp-1 mb-1 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>{isAr ? item.name_ar || item.name : item.name}</h4>
                    <div className="text-[#F59E0B] font-bold text-sm"><PriceDisplay price={item.price} /></div>
                  </div>
                </Link>
              ))}
            </HorizontalSlider>
          </div>
          )}
        </div>
      </section>

      {/* Loyalty Program */}
      <section className={`py-6 sm:py-8 z-10 relative ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-4 sm:mb-6 lg:mb-10">
            <div className={`inline-flex items-center gap-2 ${isDarkMode ? 'bg-[#F59E0B]/10 border-[#F59E0B]/20' : 'bg-[#F59E0B]/5 border-[#F59E0B]/10'} border px-4 py-2 rounded-full mb-6`}>
              <Crown className="h-4 w-4 text-[#F59E0B]" />
              <span className="text-sm text-[#F59E0B] font-medium">{isAr ? 'برنامج الولاء' : 'Loyalty Program'}</span>
            </div>
            <h2 className={`text-3xl sm:text-4xl font-light mb-4 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
              {isAr ? 'اكسب نقاط مع كل تعامل' : 'Earn Points with Every Transaction'}
            </h2>
            <p className={`max-w-2xl mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {isAr ? 'انضم لبرنامج الولاء واستمتع بمزايا حصرية' : 'Join our loyalty program and enjoy exclusive benefits'}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {loyaltyTiers.map((tier, index) => (
              <LoyaltyCard key={index} tier={tier} index={index} isDarkMode={isDarkMode} isAr={isAr} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-6 sm:py-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] ${isDarkMode ? 'bg-gradient-to-r from-[#1E9BD9]/20 to-[#F59E0B]/20' : 'bg-gradient-to-r from-[#1E9BD9]/10 to-[#F59E0B]/10'} rounded-full blur-[100px]`} />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className={`text-3xl sm:text-4xl font-light mb-6 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
            {isAr ? 'جاهز للبدء؟' : 'Ready to Get Started?'}
          </h2>
          <p className={`mb-10 text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {isAr ? 'انضم إلى آلاف العملاء السعداء واستفد من خدماتنا المتميزة' : 'Join thousands of happy customers and benefit from our premium services'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white px-10 py-6 rounded-xl text-lg font-medium shadow-lg shadow-[#1E9BD9]/20">
                {isAr ? 'سجل الآن مجاناً' : 'Register for Free'}
              </Button>
            </Link>
            <Link to="/services/company-formation/chat">
              <Button variant="outline" className={`border-2 px-10 py-6 rounded-xl text-lg font-medium ${isDarkMode ? 'border-white/10 text-white hover:bg-white/5' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                {isAr ? 'تحدث مع المساعد الذكي' : 'Talk to AI Assistant'}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Seller CTA — moved from Marketplace to reach a bigger audience */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] ${isDarkMode ? 'bg-gradient-to-r from-[#1E9BD9]/20 to-[#F59E0B]/20' : 'bg-gradient-to-r from-[#1E9BD9]/10 to-[#F59E0B]/10'} rounded-full blur-[80px]`} />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 ${isDarkMode ? 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30' : 'bg-[#F59E0B]/10 text-[#B45309] border border-[#F59E0B]/30'}`}>
            {isAr ? 'انضم إلى آلاف التجار' : 'Join thousands of merchants'}
          </div>
          <h2 className={`text-3xl md:text-4xl font-light mb-4 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
            {isAr ? 'هل لديك ما تبيعه؟' : 'Have Something to Sell?'}
          </h2>
          <p className={`mb-8 text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {isAr
              ? 'انضم إلى منصتنا وابدأ ببيع أرقامك ولوحاتك وسياراتك وعقاراتك ومنتجاتك اليوم'
              : 'Join our platform and start selling your numbers, plates, cars, properties and products today'}
          </p>
          <a href="https://cloudgm.business/register" target="_blank" rel="noopener noreferrer" data-testid="home-seller-cta">
            <Button className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white px-10 py-6 rounded-xl text-lg font-medium shadow-lg shadow-[#1E9BD9]/20">
              {isAr ? 'سجل كتاجر' : 'Register as Merchant'}
            </Button>
          </a>
        </div>
      </section>

      {/* Item Details Modal */}
      <ItemDetailsModal 
        item={selectedItem}
        isOpen={showItemModal}
        onClose={closeItemModal}
        onFavoriteToggle={toggleFavorite}
        isFavorite={selectedItem ? isFavorite(selectedItem.id) : false}
      />
    </div>
  );
}
